/**
 * Navigation reducer — manages the hierarchical tab structure and active tab state.
 *
 * The app has two distinct navigation trees:
 *   - Pre-analysis (initialTabs_pre): Ingestion -> Parameters -> Analysis flow
 *   - Post-analysis (initialTabs_post): Dashboard -> Full Analysis -> Parameters -> Ingestion
 *
 * Tabs are nested up to 3 levels deep. The "active" flag on each tab node determines
 * the current path through the tree. The leaf tab (deepest active) drives which
 * screen component is rendered by Project/index.js via the Screens map.
 */
const initialTabs_pre = [
  {
    tab: "ingestion",
    headline: "Ingestion",
    iconclass: "fa-duotone fa-upload",
    active: true,
    tabs: [
      {
        tab: "upload",
        headline: "Upload",
        iconclass: "fa-duotone fa-cloud-arrow-up",
        active: true,
      },
      {
        tab: "files",
        headline: "Files",
        iconclass: "fa-duotone fa-files",
      },
      {
        tab: "collections",
        headline: "Data Groups",
        iconclass: "fa-duotone fa-box",
        tabs: [
          {
            tab: "canonicals",
            headline: "Column Mapping",
            iconclass: "fa-duotone fa-route",
            active: true,
          },
        ],
      },
      {
        tab: "frame",
        headline: "Frame",
        iconclass: "fa-duotone fa-microchip",
        tabs: [
          {
            tab: "components",
            headline: "Components",
            iconclass: "fa-duotone fa-layer-group",
            active: true,
          },
          {
            tab: "scrub",
            headline: "Scrub",
            iconclass: "fa-duotone fa-broom",
          },
          {
            tab: "filters",
            headline: "Filters",
            iconclass: "fa-duotone fa-filters",
          },
          {
            tab: "master",
            headline: "Master Data",
            iconclass: "fa-duotone fa-database",
          },
        ],
      },
      {
        tab: "documenttypes",
        headline: "Document Types",
        iconclass: "fa-duotone fa-file-lines",
      },
      {
        tab: "buyerratings",
        headline: "Buyer Ratings",
        iconclass: "fa-duotone fa-ranking-star",
      },
    ]
  },
  {
    tab: "parameters",
    headline: "Parameters & Launch",
    iconclass: "fa-duotone fa-sliders",
  },
  {
    tab: "analysis",
    headline: "Analysis",
    iconclass: "fa-duotone fa-book-open-cover",
    tabs: [
      {
        tab: "balancesandactivities",
        headline: "Balances and Activities",
        iconclass: "fa-duotone fa-chart-line",
        active: true,
        tabs: [
          {
            tab: "aging",
            headline: "Aging",
            iconclass: "fa-duotone fa-timeline",
            active: true,
          },
          {
            tab: "rollforward",
            headline: "Rollforward",
            iconclass: "fa-duotone fa-code-compare",
          },
          {
            tab: "rollforwardanalysis",
            headline: "Rollforward Analysis",
            iconclass: "fa-duotone fa-magnifying-glass-chart",
          },

        ]
      },
      {
        tab: "eligibilitypre",
        headline: "Eligibility",
        iconclass: "fa-duotone fa-building",
        tabs: [
          {
            tab: "buyerparent",
            headline: "Accounts Receivable",
            iconclass: "fa-duotone fa-file-invoice-dollar",
            active: true,
          },
          {
            tab: "concentrationlimits",
            headline: "Concentration Limits",
            iconclass: "fa-duotone fa-chart-pie",
          },
          {
            tab: "nerb",
            headline: "NERB",
            iconclass: "fa-duotone fa-chart-line",
          },
        ],
      },
      {
        tab: "reserves",
        headline: "Reserves",
        iconclass: "fa-duotone fa-vault",
        tabs: [
          {
            tab: "lossreserve",
            headline: "Loss Reserve",
            iconclass: "fa-duotone fa-circle-exclamation",
            active: true,
          },
          {
            tab: "dilution",
            headline: "Dilution Reserve",
            iconclass: "fa-duotone fa-droplet",
          },
          {
            tab: "yieldreserve",
            headline: "Yield Reserve",
            iconclass: "fa-duotone fa-percent",
          },
          {
            tab: "aggregatereserve",
            headline: "Aggregate Reserve",
            iconclass: "fa-duotone fa-layer-group",
          },
        ],
      },
    ],
  },
  {
    tab: "fraud",
    headline: "Fraud Detection",
    iconclass: "fa-duotone fa-shield-halved",
  },
  {
    tab: "compare",
    headline: "Compare",
    iconclass: "fa-duotone fa-scale-balanced",
  },
  {
    tab: "report",
    headline: "Report",
    iconclass: "fa-duotone fa-file-chart-column",
  },
  {
    tab: "downloads",
    headline: "Downloads",
    iconclass: "fa-duotone fa-download",
  },
];

const initialTabs_post = [
  {
    tab: "dashboard",
    headline: "Dashboard",
    iconclass: "fa-duotone fa-chart-mixed",
    active: true,
  },
  {
    tab: "fullanalysis",
    headline: "Full Analysis",
    iconclass: "fa-duotone fa-book-open-cover",
    tabs: [
      {
        tab: "aging",
        headline: "Aging",
        iconclass: "fa-duotone fa-timeline",
        active: true,
        tabs: [
          {
            tab: "aging",
            headline: "Aging Timeseries",
            iconclass: "fa-duotone fa-timeline",
            active: true,
          },
          {
            tab: "agingchart",
            headline: "Aging Chart",
            iconclass: "fa-duotone fa-chart-line",
          },
        ],
      },
      {
        tab: "rollforward",
        headline: "Rollforward",
        iconclass: "fa-duotone fa-code-compare",
      },
      {
        tab: "rollforwardanalysis",
        headline: "Rollforward Analysis",
        iconclass: "fa-duotone fa-magnifying-glass-chart",
      },
      {
        tab: "eligibilitypost",
        headline: "Eligibility",
        iconclass: "fa-duotone fa-building",
        tabs: [
          {
            tab: "buyerparent",
            headline: "Accounts Receivable",
            iconclass: "fa-duotone fa-file-invoice-dollar",
            active: true,
          },
          {
            tab: "concentrationlimits",
            headline: "Concentration Limits",
            iconclass: "fa-duotone fa-chart-pie",
          },
          {
            tab: "nerb",
            headline: "NERB",
            iconclass: "fa-duotone fa-chart-line",
          },
        ],
      },
      {
        tab: "reserves",
        headline: "Reserves",
        iconclass: "fa-duotone fa-vault",
        tabs: [
          {
            tab: "lossreserve",
            headline: "Loss Reserve",
            iconclass: "fa-duotone fa-circle-exclamation",
            active: true,
          },
          {
            tab: "dilution",
            headline: "Dilution Reserve",
            iconclass: "fa-duotone fa-droplet",
          },
          {
            tab: "yieldreserve",
            headline: "Yield Reserve",
            iconclass: "fa-duotone fa-percent",
          },
          {
            tab: "aggregatereserve",
            headline: "Aggregate Reserve",
            iconclass: "fa-duotone fa-layer-group",
          },
        ],
      },
      {
        tab: "eligibility",
        headline: "Eligibility",
        iconclass: "fa-duotone fa-handshake-simple",
        tabs: [
          {
            tab: "supplier",
            headline: "Supplier Metrics",
            iconclass: "fa-duotone fa-boxes-packing",
            active: true,
          },
          {
            tab: "invoice",
            headline: "Invoice Eligibility",
            iconclass: "fa-duotone fa-file-invoice",
          },
          {
            tab: "performance",
            headline: "Performance Ratios",
            iconclass: "fa-duotone fa-percent",
          },
          {
            tab: "criteria",
            headline: "Supplier Eligibility Criteria",
            iconclass: "fa-duotone fa-badge-check",
          },
        ],
      },
      {
        tab: "advance",
        headline: "Advance Rate",
        iconclass: "fa-duotone fa-file-invoice-dollar",
      },
    ],
  },
  {
    tab: "fraud",
    headline: "Fraud Detection",
    iconclass: "fa-duotone fa-shield-halved",
  },
  {
    tab: "compare",
    headline: "Compare",
    iconclass: "fa-duotone fa-scale-balanced",
  },
  {
    tab: "report",
    headline: "Report",
    iconclass: "fa-duotone fa-file-chart-column",
  },
  {
    tab: "downloads",
    headline: "Downloads",
    iconclass: "fa-duotone fa-download",
  },
  {
    tab: "parameters",
    headline: "Parameters & Launch",
    iconclass: "fa-duotone fa-sliders",
  },
  {
    tab: "ingestion",
    headline: "Ingestion",
    iconclass: "fa-duotone fa-upload",
    tabs: [
      {
        tab: "upload",
        headline: "Upload",
        iconclass: "fa-duotone fa-cloud-arrow-up",
        active: true,
      },
      {
        tab: "files",
        headline: "Files",
        iconclass: "fa-duotone fa-files",
      },
      {
        tab: "collections",
        headline: "Data Groups",
        iconclass: "fa-duotone fa-box",
        tabs: [
          {
            tab: "canonicals",
            headline: "Column Mapping",
            iconclass: "fa-duotone fa-route",
            active: true,
          },
        ],
      },
      {
        tab: "frame",
        headline: "Frame",
        iconclass: "fa-duotone fa-microchip",
        tabs: [
          {
            tab: "components",
            headline: "Components",
            iconclass: "fa-duotone fa-layer-group",
            active: true,
          },
          {
            tab: "scrub",
            headline: "Scrub",
            iconclass: "fa-duotone fa-broom",
          },
          {
            tab: "filters",
            headline: "Filters",
            iconclass: "fa-duotone fa-filters",
          },
          {
            tab: "master",
            headline: "Master Data",
            iconclass: "fa-duotone fa-database",
          },
        ],
      },
      {
        tab: "documenttypes",
        headline: "Document Types",
        iconclass: "fa-duotone fa-file-lines",
      },
      {
        tab: "buyerratings",
        headline: "Buyer Ratings",
        iconclass: "fa-duotone fa-ranking-star",
      },
    ]
  },
];

// -------------------------------------------------------
// -------------------------------------------------------
// Payables navigation trees
// -------------------------------------------------------
const initialTabs_payables_pre = [
  {
    tab: "ingestion",
    headline: "Ingestion",
    iconclass: "fa-duotone fa-upload",
    active: true,
    tabs: [
      {
        tab: "upload",
        headline: "Upload",
        iconclass: "fa-duotone fa-cloud-arrow-up",
        active: true,
      },
      {
        tab: "files",
        headline: "Files",
        iconclass: "fa-duotone fa-files",
      },
      {
        tab: "collections",
        headline: "Data Groups",
        iconclass: "fa-duotone fa-box",
        tabs: [
          {
            tab: "canonicals",
            headline: "Column Mapping",
            iconclass: "fa-duotone fa-route",
            active: true,
          },
        ],
      },
      {
        tab: "frame",
        headline: "Frame",
        iconclass: "fa-duotone fa-microchip",
        tabs: [
          {
            tab: "components",
            headline: "Components",
            iconclass: "fa-duotone fa-layer-group",
            active: true,
          },
          {
            tab: "scrub",
            headline: "Scrub",
            iconclass: "fa-duotone fa-broom",
          },
          {
            tab: "filters",
            headline: "Filters",
            iconclass: "fa-duotone fa-filters",
          },
          {
            tab: "master",
            headline: "Master Data",
            iconclass: "fa-duotone fa-database",
          },
        ],
      },
      {
        tab: "documenttypes",
        headline: "Document Types",
        iconclass: "fa-duotone fa-file-lines",
      },
    ],
  },
  {
    tab: "parameters",
    headline: "Parameters & Launch",
    iconclass: "fa-duotone fa-sliders",
  },
  {
    tab: "analysis",
    headline: "Analysis",
    iconclass: "fa-duotone fa-book-open-cover",
    tabs: [
      {
        tab: "balancesandactivities",
        headline: "Balances and Activities",
        iconclass: "fa-duotone fa-chart-line",
        active: true,
        tabs: [
          {
            tab: "aging",
            headline: "Aging",
            iconclass: "fa-duotone fa-timeline",
            active: true,
          },
          {
            tab: "rollforward",
            headline: "Rollforward",
            iconclass: "fa-duotone fa-code-compare",
          },
        ],
      },
      {
        tab: "eligibility",
        headline: "Eligibility",
        iconclass: "fa-duotone fa-handshake-simple",
        tabs: [
          {
            tab: "supplier",
            headline: "Supplier Metrics",
            iconclass: "fa-duotone fa-boxes-packing",
            active: true,
          },
          {
            tab: "invoice",
            headline: "Invoice Eligibility",
            iconclass: "fa-duotone fa-file-invoice",
          },
          {
            tab: "performance",
            headline: "Performance Ratios",
            iconclass: "fa-duotone fa-percent",
          },
          {
            tab: "criteria",
            headline: "Supplier Eligibility Criteria",
            iconclass: "fa-duotone fa-badge-check",
          },
        ],
      },
      {
        tab: "advance",
        headline: "Advance Rate",
        iconclass: "fa-duotone fa-file-invoice-dollar",
      },
    ],
  },
  {
    tab: "downloads",
    headline: "Downloads",
    iconclass: "fa-duotone fa-download",
  },
];

const initialTabs_payables_post = [
  {
    tab: "dashboard",
    headline: "Dashboard",
    iconclass: "fa-duotone fa-chart-mixed",
    active: true,
  },
  {
    tab: "fullanalysis",
    headline: "Full Analysis",
    iconclass: "fa-duotone fa-book-open-cover",
    tabs: [
      {
        tab: "aging",
        headline: "Aging",
        iconclass: "fa-duotone fa-timeline",
        active: true,
      },
      {
        tab: "rollforward",
        headline: "Rollforward",
        iconclass: "fa-duotone fa-code-compare",
      },
      {
        tab: "eligibility",
        headline: "Eligibility",
        iconclass: "fa-duotone fa-handshake-simple",
        tabs: [
          {
            tab: "supplier",
            headline: "Supplier Metrics",
            iconclass: "fa-duotone fa-boxes-packing",
            active: true,
          },
          {
            tab: "invoice",
            headline: "Invoice Eligibility",
            iconclass: "fa-duotone fa-file-invoice",
          },
          {
            tab: "performance",
            headline: "Performance Ratios",
            iconclass: "fa-duotone fa-percent",
          },
          {
            tab: "criteria",
            headline: "Supplier Eligibility Criteria",
            iconclass: "fa-duotone fa-badge-check",
          },
        ],
      },
      {
        tab: "advance",
        headline: "Advance Rate",
        iconclass: "fa-duotone fa-file-invoice-dollar",
      },
    ],
  },
  {
    tab: "downloads",
    headline: "Downloads",
    iconclass: "fa-duotone fa-download",
  },
  {
    tab: "parameters",
    headline: "Parameters & Launch",
    iconclass: "fa-duotone fa-sliders",
  },
  {
    tab: "ingestion",
    headline: "Ingestion",
    iconclass: "fa-duotone fa-upload",
    tabs: [
      {
        tab: "upload",
        headline: "Upload",
        iconclass: "fa-duotone fa-cloud-arrow-up",
        active: true,
      },
      {
        tab: "files",
        headline: "Files",
        iconclass: "fa-duotone fa-files",
      },
      {
        tab: "collections",
        headline: "Data Groups",
        iconclass: "fa-duotone fa-box",
        tabs: [
          {
            tab: "canonicals",
            headline: "Column Mapping",
            iconclass: "fa-duotone fa-route",
            active: true,
          },
        ],
      },
      {
        tab: "frame",
        headline: "Frame",
        iconclass: "fa-duotone fa-microchip",
        tabs: [
          {
            tab: "components",
            headline: "Components",
            iconclass: "fa-duotone fa-layer-group",
            active: true,
          },
          {
            tab: "scrub",
            headline: "Scrub",
            iconclass: "fa-duotone fa-broom",
          },
          {
            tab: "filters",
            headline: "Filters",
            iconclass: "fa-duotone fa-filters",
          },
          {
            tab: "master",
            headline: "Master Data",
            iconclass: "fa-duotone fa-database",
          },
        ],
      },
      {
        tab: "documenttypes",
        headline: "Document Types",
        iconclass: "fa-duotone fa-file-lines",
      },
    ],
  },
];

// -------------------------------------------------------
// -------------------------------------------------------
// Recursively walks the tab tree following "active" flags to build the breadcrumb path.
// Returns an array of { ancestor, tab, headline, hasSubTabs } from root to the deepest active tab.
const getPath = (tabs, ancestor, path) => {
  const newTabs = JSON.parse(JSON.stringify(tabs));
  for (var ii = 0; ii < newTabs.length; ii++) {
    const thisTab = newTabs[ii];
    if (thisTab.active) {
      const hasSubTabs = thisTab.hasOwnProperty("tabs");
      path.push({
        ancestor,
        tab: thisTab.tab,
        headline: thisTab.headline,
        hasSubTabs,
      });
      if (hasSubTabs) {
        getPath(thisTab.tabs, thisTab.tab, path);
      }
    }
  }
  return path;
};

// -------------------------------------------------------
// -------------------------------------------------------
// initial state is computed in SET_PRE_OR_POST action

// -------------------------------------------------------
// -------------------------------------------------------
const reducer = (state = {}, action) => {
  let mainTabs;
  // -------------------------------------------------------
  switch (action.type) {
    // -------------------------------------------------------
    // Initializes navigation with either pre-analysis or post-analysis tab tree
    case "SET_PRE_OR_POST":
      const initialTabs =
        action.projectType === "payables"
          ? (action.preOrPost === "pre" ? initialTabs_payables_pre : initialTabs_payables_post)
          : (action.preOrPost === "pre" ? initialTabs_pre : initialTabs_post);
      const initialPath = getPath(initialTabs, "root", []);
      return {
        ...state,
        mainTabs: initialTabs,
        path: initialPath,
        leaf: initialPath.at(-1).tab,
        initialized: true,
      };
    // -------------------------------------------------------
    case "SET_COLLECTION":
      mainTabs = setProcessTab([...state.mainTabs], action.collection.headline);
      return {
        ...state,
        collectionid: action.collection.id,
        mainTabs,
      };

    // --------------------------------------------------------------
    case "SET_ACTIVE_TAB":
      mainTabs = setActive(
        action.current,
        action.ancestor,
        [...state.mainTabs],
        action.ancestor === "root"
      );
      const path = getPath(mainTabs, "root", []);
      const leaf = path.at(-1).tab;
      return {
        ...state,
        mainTabs,
        leaf,
        path,
      };
    // -------------------------------------------------------
    default:
      return state;
  }
};

// -------------------------------------------------------
// -------------------------------------------------------
// Recursively sets the active tab within a specific ancestor's children.
// "ancestral" flag indicates we're at the correct nesting level to toggle siblings.
// Only deactivates sibling tabs at the matching ancestor level — deeper tabs retain their state.
const setActive = (currentTab, ancestor, tabs, ancestral) => {
  const newTabs = JSON.parse(JSON.stringify(tabs));
  for (var ii = 0; ii < newTabs.length; ii++) {
    const thisTab = newTabs[ii];
    newTabs[ii] =
      thisTab.tab === currentTab
        ? { ...thisTab, active: true }
        : ancestral
          ? { ...thisTab, active: false }
          : thisTab;
    if (thisTab.tabs) {
      newTabs[ii].tabs = setActive(
        currentTab,
        ancestor,
        thisTab.tabs,
        thisTab.tab === ancestor
      );
    }
  }
  return newTabs;
};

// -------------------------------------------------------
// -------------------------------------------------------
// Makes the "process" tab visible and sets its headline (used when a collection is selected for processing)
const setProcessTab = (tabs, headline) => {
  const newTabs = JSON.parse(JSON.stringify(tabs));
  for (var ii = 0; ii < newTabs.length; ii++) {
    const thisTab = newTabs[ii];
    if (thisTab.tab === "process") {
      newTabs[ii].invisible = false;
      newTabs[ii].headline = headline;
    }
    if (thisTab.tabs) {
      newTabs[ii].tabs = setProcessTab(thisTab.tabs, headline);
    }
  }
  return newTabs;
};

export default reducer;
